"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function IntelligentDataAutomation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: stepsRef,
    isVisible: stepsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const steps = [{
    number: "01",
    title: "Data Assessment",
    description: "We conduct a deep-dive audit of your existing infrastructure to identify bottlenecks and untapped potential within your data streams.",
    detail: "Comprehensive audit report",
    mascot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705424/img/data_audit_analysis.png",
    mascotAlt: "Abstract visualization of data audit and analysis"
  }, {
    number: "02",
    title: "System Integration",
    description: "Our engineers deploy custom automation pipelines that bridge your disparate systems into a unified, high-performance architecture.",
    detail: "Seamless API integration",
    mascot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705424/img/automation_pipeline.webp",
    mascotAlt: "Abstract representation of automated data pipelines"
  }, {
    number: "03",
    title: "Actionable Insights",
    description: "Transform raw metrics into strategic intelligence with real-time dashboards designed for executive decision-making.",
    detail: "Real-time dashboard access",
    mascot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705424/img/intelligence_dashboard.webp",
    mascotAlt: "Abstract representation of intelligence dashboard"
  }];
  return <section data-section-id="1075" id="intelligent-data-automation" className="dark text-foreground relative overflow-hidden py-28 md:py-36 bg-accent">
      {}
      

      {}
      <div className="absolute top-40 -left-32 w-[480px] h-[480px] rounded-lg opacity-30 blur-3xl pointer-events-none" style={{
      backgroundColor: "hsl(199 89% 48% / 0.2)"
    }} aria-hidden="true" />
      <div className="absolute bottom-20 -right-40 w-[560px] h-[560px] rounded-lg opacity-25 blur-3xl pointer-events-none" style={{
      backgroundColor: "hsl(217 91% 60% / 0.2)"
    }} aria-hidden="true" />

      <div className="relative max-w-[1320px] mx-auto px-5 md:px-8 lg:px-12">
        {}
        <div ref={headerRef} className={`max-w-[720px] mx-auto text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          
          <h2 className="font-default font-semibold text-foreground leading-[1.05]" style={{
          fontSize: "clamp(2rem, 4.2vw, 3.75rem)",
          letterSpacing: "-0.02em"
        }}>
            Precision engineering for{" "}
            <span className="font-default inline-block" style={{
            color: "var(--secondary)"
          }}>
              modern enterprises
            </span>
          </h2>
          <p className="font-default mt-6 text-foreground/80 leading-relaxed" style={{
          fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)"
        }}>
            Infinix Solutions bridges the gap between raw data and strategic action through rigorous automation and analytical precision.
          </p>
        </div>

        {}
        <div ref={stepsRef} className="relative mt-20 md:mt-24">
          {}
          <svg className="hidden md:block absolute top-[120px] left-0 w-full pointer-events-none" height="80" viewBox="0 0 1200 80" preserveAspectRatio="none" aria-hidden="true">
            <path id="forest-path" d="M 200,40 Q 350,10 500,40 T 800,40 Q 950,70 1000,40" fill="none" stroke="var(--secondary)" strokeWidth="2" strokeDasharray="2 8" strokeLinecap="round" opacity="0.6" />
            {}
            <g style={{
            color: "var(--secondary)"
          }}>
              <circle r="5" fill="currentColor">
                <animateMotion dur="8s" repeatCount="indefinite" keyTimes="0;0.12;0.13;0.5;0.51;0.87;0.88;1" keyPoints="0;0.5;0.5;0.5;1;1;0;0" calcMode="linear">
                  <mpath href="#forest-path" />
                </animateMotion>
              </circle>
            </g>
          </svg>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8 relative">
            {steps.map((step, i) => <div data-index={i} key={i} className={`relative transition-all duration-700 ease-out ${stepsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                {}
                {i < steps.length - 1 && <div className="md:hidden absolute left-1/2 -bottom-8 -translate-x-1/2 h-8 w-px" style={{
              backgroundImage: "radial-gradient(circle, var(--secondary) 1.5px, transparent 1.5px)",
              backgroundSize: "4px 8px",
              backgroundRepeat: "repeat-y",
              opacity: 0.6
            }} aria-hidden="true" />}

                <div className="relative rounded-lg p-8 h-full shadow-md" style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)"
            }}>
                  {}
                  <img src={step.mascot} data-wvc-srcset={`${step.mascot}-wvc-srcset`} data-wvc-sizes={`${step.mascot}-wvc-sizes`} alt={step.mascotAlt} loading="lazy" decoding="async" className="absolute top-3 right-3 w-[88px] h-[88px] md:w-[100px] md:h-[100px] object-contain pointer-events-none opacity-80" />

                  {}
                  <div className="font-mono font-bold leading-none mb-6" style={{
                fontSize: "clamp(2rem, 3vw, 3rem)",
                color: "var(--secondary)"
              }}>
                    {step.number}
                  </div>

                  <h3 className="font-default font-semibold text-foreground mb-4 pr-20" style={{
                fontSize: "clamp(1.375rem, 2vw, 1.875rem)",
                letterSpacing: "-0.01em",
                lineHeight: "1.2"
              }}>
                    {step.title}
                  </h3>

                  <p className="font-default text-foreground/75 leading-relaxed mb-6" style={{
                fontSize: "1rem"
              }}>
                    {step.description}
                  </p>

                  {}
                  <div className="flex items-center gap-2.5 pt-5 border-t border-border">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" stroke="currentColor" className="flex-shrink-0 !w-[30px] !bg-transparent">
                  <path d="M22 39C18 35 16 30 16 25C16 16 23 9 32 9C41 9 48 16 48 25C48 30 46 35 42 39C39 42 38 45 38 48H26C26 45 25 42 22 39Z" />
                  <path d="M26 54H38" />
                  <path d="M28 48H36" />
                  <path d="M32 9V4" />
                  <path d="M12 14L8 10" />
                  <path d="M52 14L56 10" />
                  <path d="M10 29H4" />
                  <path d="M60 29H54" />
                </svg>
                    <span className="font-default text-foreground/70" style={{
                  fontSize: "0.85rem",
                  fontWeight: 600
                }}>
                      {step.detail}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {}
        <div ref={ctaRef} className={`mt-20 md:mt-24 flex flex-col items-center transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button size="lg" className="rounded-lg px-8 py-6 text-base font-bold hover:scale-[1.02] transition-transform duration-200" style={{
          backgroundColor: "var(--secondary)",
          color: "var(--secondary-foreground)"
        }}>
            Get in touch
            <ArrowRight className="ml-1 w-5 h-5" />
          </Button>
          <p className="font-default mt-4 text-foreground/60" style={{
          fontSize: "0.875rem"
        }}>Schedule a consultation  for a quota based on your budget</p>
        </div>
      </div>

      {}
      <svg className="absolute bottom-0 left-0 w-full h-6 text-background rotate-180" viewBox="0 0 1440 24" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0,8 C120,2 200,16 320,10 C440,4 560,18 680,12 C800,6 920,20 1040,14 C1160,8 1280,18 1440,10 L1440,0 L0,0 Z" fill="currentColor" />
      </svg>
    </section>;
}